import { useMemo, useState, useEffect, useRef } from 'react';
import { AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer } from 'recharts';
import { ArrowUpRight, ArrowDownRight, Eye, Wallet } from 'lucide-react';
import { useCurrency } from '@/contexts';
import { useTranslation } from '@/hooks/useTranslation';
import { NET_WORTH_PERIODS, type NetWorthPeriod } from '@/hooks/useBalanceChart';

interface ChartPoint {
  date: string;
  value: number;
}

interface NetWorthBlockProps {
  netWorth: number;
  netWorthHistory: ChartPoint[];
  selectedPeriod: NetWorthPeriod;
  onPeriodChange: (period: NetWorthPeriod) => void;
  isLoading?: boolean;
  hideHeader?: boolean;
}

// Animated counter hook
function useAnimatedCounter(end: number, duration: number = 1000): number {
  const [count, setCount] = useState(0);
  const countRef = useRef(0);
  const startTime = useRef<number | null>(null);

  useEffect(() => {
    startTime.current = null;
    const startValue = countRef.current;

    const animate = (timestamp: number) => {
      if (!startTime.current) startTime.current = timestamp;
      const progress = Math.min((timestamp - startTime.current) / duration, 1);

      // Easing function for smooth animation
      const easeOutCubic = 1 - Math.pow(1 - progress, 3);
      const current = startValue + (end - startValue) * easeOutCubic;

      setCount(current);
      countRef.current = current;

      if (progress < 1) {
        requestAnimationFrame(animate);
      }
    };

    requestAnimationFrame(animate);
  }, [end, duration]);

  return count;
}

export function NetWorthBlock({
  netWorth,
  netWorthHistory,
  selectedPeriod,
  onPeriodChange,
  isLoading = false,
  hideHeader = false,
}: NetWorthBlockProps) {
  const { formatAmount, formatCompact, isSecretMode } = useCurrency();
  const { t, language } = useTranslation();
  const animatedNetWorth = useAnimatedCounter(netWorth, 1200);

  // Calculate change from first to last point
  const periodChange = useMemo(() => {
    if (netWorthHistory.length < 2) return null;
    const firstValue = netWorthHistory[0].value;
    const lastValue = netWorthHistory[netWorthHistory.length - 1].value;
    const changeAmount = lastValue - firstValue;
    const changePercent = firstValue !== 0 ? (changeAmount / Math.abs(firstValue)) * 100 : 0;
    return {
      amount: changeAmount,
      percent: Math.round(changePercent * 10) / 10,
      trend: changeAmount > 0 ? 'up' : changeAmount < 0 ? 'down' : ('neutral' as const),
    };
  }, [netWorthHistory]);

  const periodLabels = {
    '1m': t('accounts.period.1m' as any),
    '3m': t('accounts.period.3m' as any),
    '6m': t('accounts.period.6m' as any),
    '1y': t('accounts.period.1y' as any),
    all: t('accounts.period.all' as any),
  };

  return (
    <div className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-100 dark:border-gray-700/50 shadow-sm overflow-hidden">
      {/* Header */}
      <div className="p-6 pb-0">
        <div className="flex items-start justify-between mb-4">
          <div>
            {!hideHeader && (
              <div className="flex items-center gap-2 mb-2">
                <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-[#16c47f] to-emerald-600 flex items-center justify-center shadow-lg shadow-emerald-500/20">
                  <Wallet className="w-5 h-5 text-white" />
                </div>
                <div>
                  <p className="text-[10px] font-bold text-gray-400 dark:text-gray-500 uppercase tracking-[0.12em]">
                    {t('dashboard.netWorth.title')}
                  </p>
                </div>
              </div>
            )}
            <div className="flex items-baseline gap-3">
              <p className="text-[2.5rem] font-black text-gray-900 dark:text-white tracking-tight tabular-nums leading-none">
                {isSecretMode ? '•••••' : formatAmount(animatedNetWorth)}
              </p>
              {periodChange && (
                <div
                  className={`flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold ${
                    periodChange.trend === 'up'
                      ? 'bg-green-50 dark:bg-green-900/20 text-green-600 dark:text-green-400'
                      : periodChange.trend === 'down'
                        ? 'bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-400'
                        : 'bg-gray-100 dark:bg-gray-700 text-gray-500'
                  }`}
                >
                  {periodChange.trend === 'up' && <ArrowUpRight className="w-3.5 h-3.5" />}
                  {periodChange.trend === 'down' && <ArrowDownRight className="w-3.5 h-3.5" />}
                  {periodChange.trend === 'up' ? '+' : ''}
                  {isSecretMode ? '••' : formatCompact(periodChange.amount)}
                  <span className="opacity-70 ml-0.5">({periodChange.percent}%)</span>
                </div>
              )}
            </div>
            {periodChange && (
              <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">
                {t('dashboard.netWorth.overPeriod')} {periodLabels[selectedPeriod]}
              </p>
            )}
          </div>

          {/* Period Selector */}
          <div className="flex items-center gap-1 bg-gray-100/80 dark:bg-gray-700/60 rounded-xl p-1">
            {NET_WORTH_PERIODS.map((period) => (
              <button
                key={period}
                onClick={() => onPeriodChange(period)}
                className={`px-3 py-1.5 text-xs font-semibold rounded-lg transition-all ${
                  selectedPeriod === period
                    ? 'bg-white dark:bg-gray-600 text-gray-900 dark:text-white shadow-sm'
                    : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
                }`}
              >
                {periodLabels[period]}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Chart */}
      <div className="h-52 px-2">
        {isLoading ? (
          <div className="h-full flex items-center justify-center">
            <div className="w-8 h-8 border-3 border-[#16c47f] border-t-transparent rounded-full animate-spin" />
          </div>
        ) : netWorthHistory.length > 0 ? (
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={netWorthHistory} margin={{ left: 0, right: 0, top: 10, bottom: 0 }}>
              <defs>
                <linearGradient id="netWorthGradient" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#3b82f6" stopOpacity={0.8} />
                  <stop offset="100%" stopColor="#3b82f6" stopOpacity={0} />
                </linearGradient>
              </defs>
              <XAxis
                dataKey="date"
                axisLine={false}
                tickLine={false}
                tick={{ fontSize: 10, fill: '#9ca3af' }}
                tickFormatter={(d: string) => {
                  const date = new Date(d + 'T12:00:00Z');
                  return date.toLocaleDateString(language === 'fr' ? 'fr-FR' : 'en-US', {
                    day: 'numeric',
                    month: 'short',
                  });
                }}
                interval="preserveStartEnd"
                minTickGap={50}
              />
              <YAxis
                axisLine={false}
                tickLine={false}
                tick={{ fontSize: 10, fill: '#9ca3af' }}
                tickFormatter={(v: number) => (isSecretMode ? '•••' : formatCompact(v))}
                width={65}
                domain={['auto', 'auto']}
              />
              <Tooltip
                content={({ active, payload, label }) => {
                  if (!active || !payload?.[0]) return null;
                  const date = new Date(label + 'T12:00:00Z');
                  const dateStr = date.toLocaleDateString(language === 'fr' ? 'fr-FR' : 'en-US', {
                    day: 'numeric',
                    month: 'long',
                    year: 'numeric',
                  });
                  return (
                    <div
                      style={{
                        background: 'rgba(17, 24, 39, 0.95)',
                        backdropFilter: 'blur(12px)',
                        borderRadius: '14px',
                        padding: '14px 18px',
                        boxShadow: '0 20px 60px rgba(0,0,0,0.35), 0 0 0 1px rgba(255,255,255,0.06)',
                        minWidth: '180px',
                      }}
                    >
                      <div style={{ color: '#9ca3af', fontSize: '12px', marginBottom: '8px' }}>
                        {dateStr}
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <div
                          style={{
                            width: '10px',
                            height: '10px',
                            borderRadius: '50%',
                            background: '#16c47f',
                            boxShadow: '0 0 10px #16c47f80',
                            flexShrink: 0,
                          }}
                        />
                        <span style={{ color: '#e5e7eb', fontSize: '12px' }}>
                          {t('dashboard.netWorth.title')}
                        </span>
                      </div>
                      <div
                        style={{
                          color: '#fff',
                          fontSize: '20px',
                          fontWeight: 700,
                          letterSpacing: '-0.02em',
                          marginTop: '6px',
                          paddingLeft: '18px',
                        }}
                      >
                        {isSecretMode ? '•••••' : formatAmount(payload[0].value as number)}
                      </div>
                    </div>
                  );
                }}
              />
              <Area
                type="monotone"
                dataKey="value"
                stroke="#16c47f"
                strokeWidth={2.5}
                fill="url(#netWorthGradient)"
                dot={false}
                activeDot={{
                  r: 6,
                  fill: '#16c47f',
                  strokeWidth: 3,
                  stroke: '#fff',
                }}
              />
            </AreaChart>
          </ResponsiveContainer>
        ) : (
          <div className="h-full flex items-center justify-center text-gray-400 dark:text-gray-500">
            <div className="text-center">
              <Eye className="w-8 h-8 mx-auto mb-2 opacity-50" />
              <p className="text-sm">{t('dashboard.noData')}</p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
